/**
 * @fileoverview Pipeline settings manager (MOR-882)
 *
 * Per-firm pipeline configuration. Currently one setting: how often the
 * conflict-check reminder emails go out to members who haven't voted on an
 * open check. Custom fields have their own section in the sidebar. Admin/
 * owner only; `canEdit` gates the control and the mutation enforces it too.
 *
 * Visual language mirrors the Pipeline Statuses manager for consistency.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { BellRing, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { extractErrorMessage } from "@/utils/errors";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

/** Mirrors CONFLICT_REMINDER_DAY_OPTIONS in convex/preAppointment/settings.ts. */
const FREQUENCY_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Off" },
  { value: 1, label: "Every day" },
  { value: 2, label: "Every 2 days" },
  { value: 3, label: "Every 3 days" },
  { value: 7, label: "Every week" },
  { value: 14, label: "Every 2 weeks" },
];

export function PreAppointmentSettingsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: settings } = useQuery(
    convexQuery(api.preAppointment.settings.getForOrg, { orgId }),
  );
  const setFrequency = useMutation(
    api.preAppointment.settings.setConflictReminderFrequency,
  );

  const handleChange = async (value: string) => {
    const frequencyDays = Number(value);
    try {
      await setFrequency({ orgId, frequencyDays });
      toast.success(
        frequencyDays === 0
          ? "Conflict check reminders turned off."
          : "Reminder frequency saved.",
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the setting."));
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to change pipeline settings.
            </p>
          </div>
        </div>
      )}

      <div className={GROUP_CARD_CLASS}>
        <div className="border-b border-border/50 p-6">
          <h1 className="text-lg font-semibold text-primary">
            Pipeline Settings
          </h1>
          <p className="mt-1 text-sm text-primary/60">
            Firm-wide configuration for the pre-appointment pipeline.
          </p>
        </div>

        <div className="flex items-start justify-between gap-6 p-6">
          <div className="flex min-w-0 items-start gap-3">
            <BellRing className="mt-0.5 size-4 shrink-0 text-primary/60" />
            <div className="min-w-0">
              <Label
                htmlFor="conflict-reminder-frequency"
                className="text-sm font-medium"
              >
                Conflict check vote reminders
              </Label>
              <p className="mt-1 text-xs text-primary/60">
                Members who haven't voted on an open conflict check are
                re-emailed their vote link at this frequency. You can also nudge
                an individual member from the check itself.
              </p>
            </div>
          </div>
          <Select
            value={String(settings?.conflictReminderFrequencyDays ?? 3)}
            onValueChange={(value) => void handleChange(value)}
            disabled={!canEdit || settings === undefined}
          >
            <SelectTrigger
              id="conflict-reminder-frequency"
              className="w-40 shrink-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
